import React from "react";
import {
  Typography,
  TextField,
  Box,
  Stack,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { motion } from "framer-motion";
import SearchIcon from "@mui/icons-material/Search";
import { provinceOptions, countryOptions } from "../data/searchOptions";
import SearchResult from "../components/SearchResult";

const containerVariants = {
  initial: {},
  animate: {
    transition: {
      staggerChildren: 0.1,
      when: "beforeChildren",
    },
  },
};

const itemVariants = {
  initial: { opacity: 0, y: 20 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3, ease: "easeInOut" },
  },
};

function Search() {
  const [province, setProvince] = React.useState("");
  const [country, setCountry] = React.useState("");
  const [name, setName] = React.useState("");
  const [address, setAddress] = React.useState("");
  const [isSearched, setIsSearched] = React.useState(false);
  const [searchRequest, setSearchRequest] = React.useState(null);

  const handleSearch = () => {
    const finalAddress =
      `${address}, ${province}${province && country ? ", " : ""}${country}`;
    const requestPayload = {
      address: finalAddress,
      company_name: name,
    };
    setSearchRequest(requestPayload);
    setIsSearched(true);
  };

  const sharedInputSx = {
    height: "56px",
    borderRadius: "50px",
    backgroundColor: "rgba(255, 255, 255, 0.15)",
    backdropFilter: "blur(8px)",
    border: "1px solid rgba(255, 255, 255, 0.3)",
    color: "#1C2758",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.2)",
    },
    "&.Mui-focused": {
      borderColor: "#1C2758",
      boxShadow: "0 0 0 2px rgba(28, 39, 88, 0.3)",
    },
  };

  const sharedLabelSx = {
    left: "9px",
    top: "-3px",
    color: "#1C2758",
    "&.Mui-focused": {
      color: "#1C2758",
    },
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        background: "linear-gradient(135deg, #afafafc4 0%, #F4F6FB 100%)",
        display: "flex",
        flexDirection: "column",
        alignItems: isSearched ? "flex-start" : "center",
        justifyContent: isSearched ? "flex-start" : "center",
        mt: isSearched ? "0px" : "0rem",
        px: 2,
        py: 4,
        boxSizing: "border-box",
      }}
    >
      <motion.div
        variants={containerVariants}
        initial="initial"
        animate="animate"
        layout
        transition={{ duration: 0.3, ease: "easeInOut" }}
        style={{
          width: "100%",
          maxWidth: 1200,
          padding: "1.5rem",
          paddingBottom: "0px",
          marginTop: isSearched ? "0px" : "-6rem",
        }}
      >
        <motion.div
          layout
          animate={{ y: isSearched ? 0 : 20 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
        >
          <Stack
            spacing={4}
            sx={{
              width: "100%",
              alignItems: isSearched ? "flex-start" : "center",
              textAlign: "left",
              marginTop: isSearched ? "1.5rem" : "0px",
              transition: "all 0.4s ease",
            }}
          >
            <motion.div variants={itemVariants}>
              <Typography
                variant={isSearched ? "h5" : "h4"}
                fontWeight="600"
                sx={{
                  color: "#1C2758",
                  marginBottom: isSearched ? "0px" : "1rem",
                  transition: "all 0.3s ease",
                  mt: isSearched ? "0" : "-2rem",
                }}
              >
                Good morning, Arron 🌞
              </Typography>
            </motion.div>

            {/* ⬇️ Wrapping inside <form> to catch Enter key */}
            <form
              onSubmit={(e) => {
                e.preventDefault(); // Prevent page reload
                handleSearch();
              }}
              style={{ width: "100%" }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 2,
                  justifyContent: isSearched ? "flex-start" : "center",
                  transition: "all 0.4s ease",
                }}
              >
                <motion.div variants={itemVariants}>
                  <TextField
                    label="Company Name"
                    fullWidth
                    autoComplete="off"
                    variant="filled"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    InputProps={{
                      disableUnderline: true,
                      sx: sharedInputSx,
                    }}
                    InputLabelProps={{
                      sx: sharedLabelSx,
                    }}
                    sx={{
                      minWidth: 250,
                      "& .MuiInputBase-input": {
                        padding: "0 20px",
                        marginTop: "5px",
                      },
                    }}
                  />
                </motion.div>

                <motion.div variants={itemVariants}>
                  <TextField
                    label="Address Line 1"
                    fullWidth
                    autoComplete="off"
                    variant="filled"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    InputProps={{
                      disableUnderline: true,
                      sx: sharedInputSx,
                    }}
                    InputLabelProps={{
                      sx: sharedLabelSx,
                    }}
                    sx={{
                      minWidth: 250,
                      "& .MuiInputBase-input": {
                        padding: "0 20px",
                        marginTop: "5px",
                      },
                    }}
                  />
                </motion.div>

                <motion.div variants={itemVariants}>
                  <FormControl variant="filled" sx={{ minWidth: 250 }}>
                    <InputLabel id="province-label" sx={sharedLabelSx}>
                      State
                    </InputLabel>
                    <Select
                      labelId="province-label"
                      value={province}
                      onChange={(e) => setProvince(e.target.value)}
                      disableUnderline
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            borderRadius: "16px",
                            backgroundColor: "rgba(255, 255, 255, 0.15)",
                            backdropFilter: "blur(6px)",
                          },
                        },
                      }}
                      sx={{
                        ...sharedInputSx,
                        "& .MuiSelect-select": {
                          paddingLeft: "20px",
                          marginTop: "-5px",
                          color: "#1C2758",
                        },
                      }}
                    >
                      {provinceOptions.map((prov) => (
                        <MenuItem key={prov} value={prov}>
                          {prov}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </motion.div>

                <motion.div variants={itemVariants}>
                  <FormControl variant="filled" sx={{ minWidth: 250 }}>
                    <InputLabel id="country-label" sx={sharedLabelSx}>
                      Country
                    </InputLabel>
                    <Select
                      labelId="country-label"
                      value={country}
                      onChange={(e) => setCountry(e.target.value)}
                      disableUnderline
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            borderRadius: "16px",
                            backgroundColor: "rgba(255, 255, 255, 0.15)",
                            backdropFilter: "blur(12px)",
                          },
                        },
                      }}
                      sx={{
                        ...sharedInputSx,
                        "& .MuiSelect-select": {
                          paddingLeft: "20px",
                          marginTop: "-5px",
                          color: "#1C2758",
                        },
                      }}
                    >
                      {countryOptions.map((ctry) => (
                        <MenuItem key={ctry} value={ctry}>
                          {ctry}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </motion.div>

                <motion.div variants={itemVariants}>
                  <Button
                    type="submit"
                    variant="contained"
                    sx={{
                      borderRadius: "50px",
                      px: 4,
                      height: "56px",
                      textTransform: "none",
                      boxShadow: 3,
                      backgroundColor: "#08AA66",
                      color: "#fff",
                      "&:hover": {
                        backgroundColor: "#07995c",
                      },
                    }}
                    startIcon={<SearchIcon />}
                  >
                    Search
                  </Button>
                </motion.div>
              </Box>
            </form>
          </Stack>
        </motion.div>
      </motion.div>

      {isSearched && (
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: "easeOut", delay: 0.4 }}
          style={{ width: "100%", marginTop: "20px" }}
        >
          <SearchResult searchRequest={searchRequest} />
        </motion.div>
      )}
    </Box>
  );
}

export default Search;
